import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box
} from '@mui/material';
import { AdminPanelSettings, Warning } from '@mui/icons-material';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
function PlatformAdminDialog({ open, onClose, user, action, onSuccess }) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isGranting = action === 'grant';

  // Use full display name from workspace
  const userName = user?.displayName || user?.email || 'this user';

  const expectedText = isGranting
    ? `I understand the risks of granting ${userName} platform admin role`
    : `revoke platform admin from ${user?.email || ''}`;

  const handleConfirm = async () => {
    if (confirmText !== expectedText) {
      setError('Confirmation text does not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isGranting) {
        await api.grantPlatformAdmin(user.email);
      } else {
        await api.revokePlatformAdmin(user.email);
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.message || `Failed to ${action} platform admin privileges`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError(null);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isGranting ? (
          <>
            <AdminPanelSettings color="primary" />
            Grant Platform Admin Privileges
          </>
        ) : (
          <>
            <Warning color="error" />
            Revoke Platform Admin Privileges
          </>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {isGranting ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                This is a highly privileged action!
              </Typography>
              <Typography variant="body2" component="div">
                Platform admins have full system access including:
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Managing all entity data and permissions</li>
                  <li>Creating and modifying global templates</li>
                  <li>Accessing all reports and documents</li>
                  <li>Managing other users' permissions</li>
                  <li>Viewing all audit logs and system data</li>
                  <li>Full access to all system features without restrictions</li>
                </ul>
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                This role should only be granted to trusted system administrators.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Warning: This will remove all platform administrative privileges
              </Typography>
              <Typography variant="body2">
                The user will lose access to all administrative functions and will only retain their entity-specific roles.
                They will no longer be able to:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Access entities they don't have explicit permissions for</li>
                  <li>Manage other users' permissions</li>
                  <li>Create or modify global templates</li>
                  <li>View system-wide audit logs</li>
                </ul>
              </Typography>
            </Alert>
          )}

          <Typography variant="body1" gutterBottom>
            You are about to {isGranting ? 'grant platform admin privileges to' : 'revoke platform admin privileges from'}:
            <Box component="span" sx={{ fontWeight: 'bold', display: 'block', mt: 1 }}>
              {user.displayName} ({user.email})
            </Box>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
            To confirm this action, please type:
            <code style={{
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'block',
              marginTop: '8px',
              fontSize: '14px',
              wordBreak: 'break-word'
            }}>
              {expectedText}
            </code>
          </Typography>

          <TextField
            fullWidth
            label="Confirmation Text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            error={!!error}
            helperText={error}
            autoFocus
            disabled={loading}
            placeholder={`Type the confirmation text above`}
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={isGranting ? 'error' : 'warning'}
          variant="contained"
          disabled={loading || confirmText !== expectedText}
        >
          {loading ? 'Processing...' : isGranting ? 'Grant Platform Admin' : 'Revoke Platform Admin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PlatformAdminDialog;